import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { sortBy } from 'underscore';
import { showToast } from 'stores/ToastStore';
import { getAdminUsersQuery } from 'utils/v2/UserWebAPIUtilsV2';
import { updateCarrier } from 'utils/CustomersAPIUtils';
import { getCustomerQuery } from 'utils/v2/CustomersAPIUtilsV2';
import LayoutWithSidebar from 'components/v2/LayoutWithSidebar';
import Spinner from 'components/ui/Spinner';
import Sidebar from 'components/modules/carrier/AdminSidebar';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Commercial from './Commercial';
import Operation from './Operation';
export function useAdminUsers() {
    const query = useQuery({
        queryKey: ['getAdminUsersQuery', { active: true }],
        queryFn: getAdminUsersQuery,
        staleTime: 120000,
    });
    const { data, error, isLoading, isError, isSuccess, status, isRefetching } = query;
    return { data, error, isLoading, isError, isSuccess, status, isRefetching };
}
export function useCustomerById(id) {
    const query = useQuery({
        queryKey: ['getCustomerQuery', id],
        queryFn: getCustomerQuery,
    });
    const { data, error, isLoading, isError, isSuccess, status, isRefetching } = query;
    return { data, error, isLoading, isError, isSuccess, status, isRefetching };
}
const CarrierTeam = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient({});
    const { id: userId } = useParams();
    const [user, setUser] = useState(null);
    const [commercialData, setCommercialData] = useState({});
    const [operationsData, setOperationsData] = useState({});
    const { data: adminData, isSuccess: adminFetchIsSuccess, isLoading: adminUserIsLoading, isError: adminUserFetchIsError, } = useAdminUsers();
    const { data: customerData, isSuccess: customerFetchIsSuccess, isLoading: customerIsLoading, isError: customerFetchIsError, } = useCustomerById(userId);
    const activeAdmins = useMemo(() => {
        if (adminFetchIsSuccess && adminData) {
            const admins = adminData.entity.data;
            return sortBy(admins.map((a) => {
                const value = a.email.trim();
                return { name: value, slug: value };
            }), 'name');
        }
        return [];
    }, [adminFetchIsSuccess, adminData]);
    useEffect(() => {
        if (customerFetchIsSuccess && customerData) {
            const user = customerData;
            setUser(user);
        }
    }, [customerData]);
    const renderSidebar = () => {
        return React.createElement(Sidebar, { userId: userId, activePage: "team" });
    };
    const breadcrumbs = {
        pages: [
            {
                name: (user === null || user === void 0 ? void 0 : user.name) || t('CARRIER'),
                href: `/users/${userId}`,
                current: false,
            },
            {
                name: t('TEAM'),
                href: `/users/${userId}/carrier-settings/team`,
                current: true,
            },
        ],
    };
    const noneCheck = (obj = {}) => {
        const data = Object.assign({}, obj);
        Object.keys(obj).forEach((k) => {
            if (obj[k] === 'none') {
                data[k] = '';
            }
        });
        return data;
    };
    const getCommercialData = (currentUser, admins = []) => {
        const { capacity_rate_manager = '' } = currentUser;
        const data = noneCheck({
            capacity_rate_manager,
        });
        setCommercialData(Object.assign(Object.assign({}, data), { admins }));
    };
    const getOperationsData = (currentUser, admins = []) => {
        const { data_entry_agent = '', carrier_success_manager = '', account_support_agent = '', } = currentUser;
        const data = noneCheck({
            data_entry_agent,
            carrier_success_manager,
            account_support_agent,
        });
        setOperationsData(Object.assign(Object.assign({}, data), { admins }));
    };
    const onSaveCarrier = async (updates = {}) => {
        const payload = Object.assign({ _id: userId }, updates);
        await updateCarrier(userId, payload);
        queryClient.invalidateQueries('getCustomerQuery');
    };
    const loading = adminUserIsLoading || customerIsLoading;
    useEffect(() => {
        if (user && activeAdmins.length) {
            getCommercialData(user, activeAdmins);
            getOperationsData(user, activeAdmins);
        }
    }, [user, activeAdmins]);
    if (adminUserFetchIsError) {
        showToast(t('CARRIER'), t('FAILED_FETCH_ADMINS'), 'error');
    }
    if (customerFetchIsError) {
        showToast(t('CARRIER'), t('FETCH_FAILED_CARRIER_DATA'), 'error');
    }
    return (React.createElement(LayoutWithSidebar, { title: t('TEAM'), breadcrumbs: breadcrumbs, sidebar: renderSidebar, className: "py-4" }, loading ? (React.createElement(Spinner, { showMessage: false })) : (React.createElement("div", { "data-testid": "carrier-team", className: "lg:grid lg:grid-cols-2 gap-4 focus-within:lg:gap-4" },
        React.createElement(Commercial, { className: "lg:mb-0 mb-4", data: commercialData, onUpdateCarrier: onSaveCarrier }),
        React.createElement(Operation, { className: "lg:mb-0 mb-4", data: operationsData, onUpdateCarrier: onSaveCarrier })))));
};
export default CarrierTeam;
